import React, { useEffect, useState, useCallback } from "react";
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, getDoc, Timestamp, updateDoc, arrayRemove, arrayUnion } from "firebase/firestore";
import { Button, TextField, CircularProgress, Chip, Switch, Tooltip, Avatar, IconButton, Alert } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../firebase";
import { FUNCTIONS } from "../config";

const TeamPage = () => {
    const { user, isAdmin, orgId } = useUserAuth();
    const [inviteEmail, setInviteEmail] = useState("");
    const [invitations, setInvitations] = useState([]);
    const [appMembers, setAppMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(null);

    // Merged team members from Cloud Function
    const [teamMembers, setTeamMembers] = useState([]);
    const [allowedSlackUsers, setAllowedSlackUsers] = useState([]);
    const [slackConnected, setSlackConnected] = useState(false);
    const [hubspotConnected, setHubspotConnected] = useState(false);
    const [slackError, setSlackError] = useState(null);
    const [hubspotError, setHubspotError] = useState(null);
    const [teamLoading, setTeamLoading] = useState(true);
    const [teamFetchError, setTeamFetchError] = useState(null);
    const [togglingUser, setTogglingUser] = useState(null);

    // Subscribe to pending invitations
    useEffect(() => {
        if (!user?.uid || !isAdmin) return;
        const q = query(
            collection(db, "invitations"),
            where("invitedBy", "==", user.uid),
            where("status", "==", "pending")
        );
        const unsub = onSnapshot(q, (snap) => {
            setInvitations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [user, isAdmin]);

    // Fetch app member details from admin's members array
    useEffect(() => {
        if (!orgId || !isAdmin) return;

        const unsub = onSnapshot(doc(db, "organizations", orgId), async (snap) => {
            const data = snap.data();
            const memberUids = data?.members || [];
            if (memberUids.length === 0) {
                setAppMembers([]);
                setMembersLoading(false);
                return;
            }
            const memberDocs = await Promise.all(
                memberUids.map(async (uid) => {
                    const mSnap = await getDoc(doc(db, "users", uid));
                    return mSnap.exists() ? { uid, ...mSnap.data() } : { uid, email: "Unknown" };
                })
            );
            setAppMembers(memberDocs);
            setMembersLoading(false);
        });

        return () => unsub();
    }, [orgId, isAdmin]);

    // Fetch merged team members from Cloud Function
    const fetchTeamMembers = useCallback(async () => {
        if (!orgId) return;
        setTeamLoading(true);
        setTeamFetchError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch(FUNCTIONS.getTeamMembers, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ orgId }),
            });
            const data = await res.json();
            if (data.error) {
                setTeamFetchError(data.error);
            } else {
                setTeamMembers(data.members || []);
                setAllowedSlackUsers(data.allowedSlackUsers || []);
                setSlackConnected(data.slackConnected || false);
                setHubspotConnected(data.hubspotConnected || false);
                setSlackError(data.slackError || null);
                setHubspotError(data.hubspotError || null);
            }
        } catch (e) {
            setTeamFetchError("Failed to load team members. Please try again.");
        } finally {
            setTeamLoading(false);
        }
    }, [orgId, user]);

    useEffect(() => {
        if (orgId && isAdmin) fetchTeamMembers();
    }, [orgId, isAdmin, fetchTeamMembers]);

    const handleInvite = async () => {
        if (!inviteEmail.trim() || !user?.uid) return;
        setSending(true);
        const token = crypto.randomUUID();
        await addDoc(collection(db, "invitations"), {
            email: inviteEmail.trim(),
            orgId: user.uid,
            invitedBy: user.uid,
            role: "member",
            status: "pending",
            token,
            createdAt: Timestamp.now(),
        });
        setInviteEmail("");
        setSending(false);
    };

    const handleRevokeInvite = async (inviteId) => {
        await deleteDoc(doc(db, "invitations", inviteId));
    };

    const handleRemoveMember = async (memberUid) => {
        if (!orgId) return;
        await updateDoc(doc(db, "organizations", orgId), {
            members: arrayRemove(memberUid),
        });
        await updateDoc(doc(db, "users", memberUid), {
            role: "member",
            orgId: "",
        });
    };

    const handleToggleQuotePermission = async (slackId) => {
        if (!orgId || !slackId) return;
        setTogglingUser(slackId);
        const isAllowed = allowedSlackUsers.includes(slackId);

        // Optimistic update
        setAllowedSlackUsers((prev) =>
            isAllowed ? prev.filter((id) => id !== slackId) : [...prev, slackId]
        );

        try {
            await updateDoc(doc(db, "organizations", orgId), {
                allowedSlackUsers: isAllowed ? arrayRemove(slackId) : arrayUnion(slackId),
            });
        } catch (e) {
            // Rollback on error
            setAllowedSlackUsers((prev) =>
                isAllowed ? [...prev, slackId] : prev.filter((id) => id !== slackId)
            );
        } finally {
            setTogglingUser(null);
        }
    };

    const handleInviteTeamMember = async (email) => {
        if (!email || !user?.uid) return;
        setSending(true);
        const token = crypto.randomUUID();
        await addDoc(collection(db, "invitations"), {
            email: email.trim(),
            orgId: user.uid,
            invitedBy: user.uid,
            role: "member",
            status: "pending",
            token,
            createdAt: Timestamp.now(),
        });
        setSending(false);
    };

    const getInviteLink = (token) => {
        const origin = window.location.origin;
        return `${origin}/signup?token=${token}`;
    };

    const handleCopyLink = (token) => {
        navigator.clipboard.writeText(getInviteLink(token));
        setCopied(token);
        setTimeout(() => setCopied(null), 2000);
    };

    // Helper: check if a person is already an app member or has a pending invite
    const getAppStatus = (email) => {
        if (user?.email?.toLowerCase() === email?.toLowerCase()) return "admin";
        const member = appMembers.find((m) => m.email?.toLowerCase() === email?.toLowerCase());
        if (member) return "member";
        const invite = invitations.find((inv) => inv.email?.toLowerCase() === email?.toLowerCase());
        if (invite) return "invited";
        return null;
    };

    if (!isAdmin) {
        return (
            <div>
                <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 32px 0" }}>Team</h1>
                <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "48px", textAlign: "center" }}>
                    <p style={{ color: "#6b7280", fontSize: "15px", margin: 0 }}>
                        Only admins can manage team members.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 32px 0" }}>Team</h1>

            {/* Warning banners */}
            {slackError && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: "8px" }}>
                    Slack: {slackError}. Reconnect Slack with updated permissions to load workspace members.
                </Alert>
            )}
            {hubspotError && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: "8px" }}>
                    HubSpot: {hubspotError}
                </Alert>
            )}
            {teamFetchError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                    {teamFetchError}
                </Alert>
            )}

            {/* Invite form */}
            <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px", marginBottom: "24px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 16px 0" }}>Invite a team member</h3>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <TextField
                        label="Email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        size="small"
                        type="email"
                        sx={{ flex: 1 }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleInvite(); }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleInvite}
                        disabled={sending || !inviteEmail.trim()}
                        style={{ backgroundColor: "#825ee7", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", padding: "8px 24px", whiteSpace: "nowrap" }}
                    >
                        {sending ? "Sending..." : "Create Invite"}
                    </Button>
                </div>
            </div>

            {/* Pending invitations */}
            {invitations.length > 0 && (
                <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px", marginBottom: "24px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 16px 0" }}>Pending Invitations</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {invitations.map((inv) => (
                            <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", backgroundColor: "#fafafa", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
                                <div>
                                    <div style={{ fontWeight: 500, fontSize: "14px", color: "#1a1a1a" }}>{inv.email}</div>
                                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>Pending</div>
                                </div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <Button
                                        size="small"
                                        onClick={() => handleCopyLink(inv.token)}
                                        startIcon={<ContentCopyIcon style={{ fontSize: "14px" }} />}
                                        sx={{ color: "#825ee7", textTransform: "none", fontSize: "12px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #ddd6fe", "&:hover": { backgroundColor: "#f5f3ff" } }}
                                    >
                                        {copied === inv.token ? "Copied!" : "Copy Link"}
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => handleRevokeInvite(inv.id)}
                                        sx={{ color: "#dc2626", textTransform: "none", fontSize: "12px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", "&:hover": { backgroundColor: "#fef2f2" } }}
                                    >
                                        Revoke
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Merged team members table */}
            <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
                        Workspace Members
                        {(slackConnected || hubspotConnected) && (
                            <span style={{ fontSize: "12px", fontWeight: 400, color: "#9ca3af", marginLeft: "8px" }}>
                                {[slackConnected && "Slack", hubspotConnected && "HubSpot"].filter(Boolean).join(" + ")}
                            </span>
                        )}
                    </h3>
                    <IconButton onClick={fetchTeamMembers} disabled={teamLoading} size="small" sx={{ color: "#825ee7" }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </div>

                {teamLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                        <CircularProgress style={{ color: "#825ee7" }} size={28} />
                    </div>
                ) : !slackConnected && !hubspotConnected ? (
                    <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
                        Connect Slack or HubSpot to see workspace members here.
                    </p>
                ) : teamMembers.length === 0 ? (
                    <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
                        No members found.
                    </p>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                            <thead>
                                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                                    <th style={thStyle}>Name</th>
                                    <th style={thStyle}>Email</th>
                                    <th style={thStyle}>Slack</th>
                                    <th style={thStyle}>HubSpot</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Quote Permission</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamMembers.map((m) => {
                                    const appStatus = getAppStatus(m.email);
                                    const isAllowed = m.slackId && allowedSlackUsers.includes(m.slackId);
                                    const isToggling = togglingUser === m.slackId;

                                    return (
                                        <tr key={m.email || m.slackId} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <td style={tdStyle}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                    {m.slackAvatar ? (
                                                        <Avatar src={m.slackAvatar} sx={{ width: 28, height: 28 }} />
                                                    ) : (
                                                        <Avatar sx={{ width: 28, height: 28, fontSize: "12px", backgroundColor: "#825ee7" }}>
                                                            {(m.name || m.email || "?")[0].toUpperCase()}
                                                        </Avatar>
                                                    )}
                                                    <span style={{ fontWeight: 500 }}>{m.name || m.email}</span>
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, color: "#6b7280" }}>{m.email}</td>
                                            <td style={tdStyle}>
                                                {m.slackId ? (
                                                    <span style={{ fontSize: "12px", color: "#1a1a1a" }}>
                                                        {m.slackDisplayName || m.slackId}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: "#d1d5db" }}>&mdash;</span>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                {m.hubspotOwnerId ? (
                                                    <span style={{ fontSize: "12px", color: "#1a1a1a" }}>{m.hubspotOwnerId}</span>
                                                ) : (
                                                    <span style={{ color: "#d1d5db" }}>&mdash;</span>
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                {m.slackId ? (
                                                    <Switch
                                                        checked={!!isAllowed}
                                                        onChange={() => handleToggleQuotePermission(m.slackId)}
                                                        disabled={isToggling}
                                                        size="small"
                                                        sx={{
                                                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#825ee7" },
                                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#825ee7" },
                                                        }}
                                                    />
                                                ) : (
                                                    <Tooltip title="Only Slack users can receive quote permissions" arrow>
                                                        <span>
                                                            <Switch checked={false} disabled size="small" />
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                {appStatus === "admin" && (
                                                    <Chip label="Admin" size="small" sx={{ backgroundColor: "#825ee7", color: "#fff", fontWeight: 600, fontSize: "10px", height: "22px" }} />
                                                )}
                                                {appStatus === "member" && (
                                                    <Chip label="Member" size="small" sx={{ backgroundColor: "#f3f4f6", color: "#6b7280", fontWeight: 500, fontSize: "10px", height: "22px" }} />
                                                )}
                                                {appStatus === "invited" && (
                                                    <Chip label="Invited" size="small" sx={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 500, fontSize: "10px", height: "22px" }} />
                                                )}
                                                {!appStatus && m.email && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => handleInviteTeamMember(m.email)}
                                                        disabled={sending}
                                                        startIcon={<PersonAddIcon style={{ fontSize: "14px" }} />}
                                                        sx={{ color: "#825ee7", textTransform: "none", fontSize: "11px", fontWeight: 500, padding: "2px 10px", borderRadius: "6px", border: "1px solid #ddd6fe", "&:hover": { backgroundColor: "#f5f3ff" } }}
                                                    >
                                                        Invite
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* App Members */}
            <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 16px 0" }}>App Members</h3>
                {membersLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                        <CircularProgress style={{ color: "#825ee7" }} size={24} />
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {/* Admin (self) */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", backgroundColor: "#f0ebfc", borderRadius: "8px", border: "1px solid #ddd6fe" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div>
                                    <div style={{ fontWeight: 500, fontSize: "14px", color: "#1a1a1a" }}>{user?.email}</div>
                                    <Chip label="Admin" size="small" sx={{ backgroundColor: "#825ee7", color: "#fff", fontWeight: 600, fontSize: "10px", height: "20px", mt: 0.5 }} />
                                </div>
                            </div>
                        </div>
                        {/* Team members */}
                        {appMembers.length === 0 ? (
                            <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
                                No team members yet. Send an invite to get started.
                            </p>
                        ) : (
                            appMembers.map((m) => (
                                <div key={m.uid} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", backgroundColor: "#fafafa", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
                                    <div>
                                        <div style={{ fontWeight: 500, fontSize: "14px", color: "#1a1a1a" }}>{m.email}</div>
                                        {m.displayName && <div style={{ fontSize: "12px", color: "#9ca3af" }}>{m.displayName}</div>}
                                        <Chip label="Member" size="small" sx={{ backgroundColor: "#f3f4f6", color: "#6b7280", fontWeight: 500, fontSize: "10px", height: "20px", mt: 0.5 }} />
                                    </div>
                                    <Button
                                        size="small"
                                        onClick={() => handleRemoveMember(m.uid)}
                                        startIcon={<PersonRemoveIcon style={{ fontSize: "14px" }} />}
                                        sx={{ color: "#dc2626", textTransform: "none", fontSize: "12px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", "&:hover": { backgroundColor: "#fef2f2" } }}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const thStyle = {
    textAlign: "left",
    padding: "10px 12px",
    fontWeight: 600,
    fontSize: "12px",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
};

const tdStyle = {
    padding: "12px",
    verticalAlign: "middle",
};

export default TeamPage;
